import { Action, State, StateContext, Store } from "@ngxs/store";
import { ContractData } from "../../interfaces/contractData";
import { Injectable } from '@angular/core';
import { ContractService } from "../../services/contract.service";
import { AlertService } from "src/app/shared/store/alert/alert.service";
import { LocalizationService } from "src/app/services/localization/localization.service";
import { ContractActions } from "./contract.actions";
import { patch } from "@ngxs/store/operators";
import { ContractMap } from "../../interfaces/contractMap.model";
import { CopyContract } from "src/app/modules/contract/models/copy-contracts.model";
import { ContractFilter } from "../../interfaces/contractFilter";
import { MappingUtilsService } from "src/app/services/utils/mapping-utils.service";
import { ZoneSelectors } from "src/app/modules/zone/store/zone.selector";
import { Zone } from "src/app/modules/zone/models/zone.model";
import { ExerciseSelectors } from "src/app/modules/exercise/store/exercise.selector";

export class ContractStateModel {
  contractsMap: ContractMap[];
  contracts: ContractData[];
  filters: {
    model: ContractFilter
  };
  copyContractsForm: {
    model: CopyContract
  }
  public isLoadingContract: boolean;
  public error: string;
}

const defaults = {
  contractsMap: null,
  contracts: null,
  filters: {
    model: null
  },
  copyContractsForm: {
    model: null
  },
  isLoadingContract: false,
  error: null
}

@State<ContractStateModel>({
  name: "contracts",
  defaults
})
@Injectable()
export class ContractState {
  constructor(
    private contractService: ContractService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store,
  ) {}

  @Action(ContractActions.GetContractsMap)
  getContractsMap(ctx: StateContext<ContractStateModel>, { }: ContractActions.GetContractsMap) {
    ctx.patchState({isLoadingContract: true});
    return this.contractService.getContractsMap().subscribe({
      next: (res) =>
        ctx.dispatch(new ContractActions.GetContractsMapSuccess(res)),
      error: (error) => {
        ctx.dispatch(new ContractActions.Error(error, ''));
      }
    });
  }

  @Action(ContractActions.GetContractsMapSuccess)
  getContractsMapSuccess(ctx: StateContext<ContractStateModel>, { contracts }: ContractActions.GetContractsMapSuccess) {
    ctx.patchState({
      contractsMap: contracts,
      isLoadingContract: false,
    });
  }

  @Action(ContractActions.GetContracts)
  getContractsByFilter(ctx: StateContext<ContractStateModel>, { exerciseId, zoneId, isStudentsContract }: ContractActions.GetContracts) {
    ctx.patchState({isLoadingContract: true});
    let isStudentsContractParam = isStudentsContract.toString();
    if (isStudentsContract == 3) { isStudentsContractParam = '' }
    return this.contractService.getContractsByFilter(exerciseId, zoneId, isStudentsContractParam).subscribe({
      next: (res) =>
        ctx.dispatch(new ContractActions.GetContractsSuccess(res)),
      error: (error) => {
        ctx.dispatch(new ContractActions.Error(error, ''));
      }
    });
  }

  @Action(ContractActions.GetContractsSuccess)
  getContractSuccess(ctx: StateContext<ContractStateModel>, { contracts }: ContractActions.GetContractsSuccess) {
    ctx.patchState({
      contracts: contracts,
      isLoadingContract: false,
    });
  }

  @Action(ContractActions.CopyContracts)
  copyContracts(ctx: StateContext<ContractStateModel>, { }: ContractActions.CopyContracts) {
    let state = ctx.getState();
    let copyContractsForm = state.copyContractsForm.model;

    let zonesMap = this.store.selectSnapshot(ZoneSelectors.getZones);
    let exercises = this.store.selectSnapshot(ExerciseSelectors.getExercises);
    let contractTypeNames = {0: this.localizationService.translate('contracts.schools'), 1: this.localizationService.translate('contracts.families')};
    let oldExcerciseDescription = exercises.find(exercise => exercise.exerciseId == copyContractsForm.oldExerciseId).description;
    let newExcerciseDescription = exercises.find(exercise => exercise.exerciseId == copyContractsForm.newExerciseId).description;

    this.alertService.confirm(
      this.localizationService.translate("contracts.copyContracts"),
      this.localizationService.translate('contracts.copyContractsConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('contracts.copyContractsConfirm')
      .replace('{0}', oldExcerciseDescription ).replace('{1}', newExcerciseDescription)
      .replace('{3}', this.getSelectedZoneNames(copyContractsForm.zones, zonesMap)).replace('{4}', contractTypeNames[copyContractsForm.contractType])
    ).then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingContract: true });
          const copyContractsFormRequest = this.getContractsFormRequest(copyContractsForm);
          return this.contractService.copyContracts(copyContractsFormRequest).subscribe({
            next: () =>
              ctx.dispatch(new ContractActions.CopyContractsSuccess()),
            error: (error) => {
              ctx.dispatch(new ContractActions.Error(error, ''));
            }
          });
        } else {
          return null;
        }
      });
  }

  getContractsFormRequest(copyContractsForm: CopyContract) {
    let newExerciseYear = Number(copyContractsForm.newExerciseId) + 1;
    let copyContractsFormRequest = {
      zones: copyContractsForm.zones,
      contractType: Number(copyContractsForm.contractType),
      newExerciseId: Number(copyContractsForm.newExerciseId),
      oldExerciseId: Number(copyContractsForm.oldExerciseId),
      schoolCourse:  this.localizationService.translate('contracts.schoolCourse')
      .replace('{0}', copyContractsForm.newExerciseId.toString()).replace('{1}', newExerciseYear.toString())
    };
    return copyContractsFormRequest;
  }

  @Action(ContractActions.CopyContractsSuccess)
  copyContractsSuccess(ctx: StateContext<ContractStateModel>, { }: ContractActions.CopyContractsSuccess) {
    ctx.patchState({
      isLoadingContract: false,
    });
  }

  @Action(ContractActions.SetFilterCurrentExercise)
  setFilterCurrentExercise(ctx: StateContext<ContractStateModel>, {exerciseId}: ContractActions.SetFilterCurrentExercise) {
    ctx.setState(
      patch({
        filters: patch({
          model: patch({
            exerciseId: exerciseId
          })
        })
      })
    );
  }

  @Action(ContractActions.SetContractsDefaultState)
  setSchoolsFormDefaultState(ctx: StateContext<ContractStateModel>, {}: ContractActions.SetContractsDefaultState) {
    ctx.patchState(
      defaults
    );
  }

  @Action(ContractActions.SetCopyContractsFormExercises)
  setCopyContractsFormExercises(ctx: StateContext<ContractStateModel>, {newExerciseId, oldExerciseId}: ContractActions.SetCopyContractsFormExercises) {
    ctx.setState(
      patch({
        copyContractsForm: patch({
          model: patch({
            newExerciseId: newExerciseId,
            oldExerciseId: oldExerciseId
          })
        })
      })
    );
  }

  @Action(ContractActions.SetCopyContractsFormDefaultState)
  setCopyContractsDefaultState(ctx: StateContext<ContractStateModel>, {}: ContractActions.SetCopyContractsFormDefaultState) {
    ctx.setState(
      patch({
        copyContractsForm: defaults.copyContractsForm
      })
    );
  }

  @Action(ContractActions.Error)
  onErrorOcurred(ctx: StateContext<ContractStateModel>, {error, details}: ContractActions.Error) {
    ctx.patchState({
      error: error,
      isLoadingContract: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  private getTitle() {
    return this.localizationService.translate("labels.contracts");
  }

  private getSelectedZoneNames(zones: number[], zonesMap: Zone[]) {
    return zones.map(zoneId => zonesMap.find(zone => zone.code === zoneId).description).join(', ');
  }
}
